import { type ExperimentInterface } from 'client/Interfaces/ExperimentsInterface'
import { type ExperimentsState } from 'client/reducers/experiments'

import { PlacementsFromPath } from '../Global/AnalyticsEvents'
import { QuotaTypes } from '../Interfaces/Subscription'
import { type SubscriptionManagementStateInterface } from '../Interfaces/Subscription/ReduxStateInterfaces'

export interface AnalyticsStateRequirementsInterface {
  token: {
    jti: string
    country: string
    prn: {
      companyId: number
      userId: number
    }
    locale: string
  }

  subscriptionManagement?: SubscriptionManagementStateInterface
  experiments?: ExperimentsState
}

/**
 * A json payload to be delivered to Tag Manger with the event
 */
export interface AnalyticsPayloadInterface {
  path?: string
  source?: string
  type?: string
  category?: string
  result?: string
  documentType?: string
  documentId?: number
  placement?: string
  deliveryType?: string

  [key: string]:
    | AnalyticsPayloadInterface
    | string
    | number
    | boolean
    | null
    | object
    | Array<string | number | boolean | null | object | AnalyticsPayloadInterface>
}

export const getUserAgent = (): string => navigator.userAgent || navigator.vendor || (window as any).opera || ''

export const getClientOS = (): string => {
  const ua = getUserAgent()

  if (/windows phone/i.test(ua)) {
    return 'Windows Phone'
  }

  if (/android/i.test(ua)) {
    return 'Android'
  }

  // iOS detection
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
    return 'iOS'
  }

  return 'unknown'
}

export interface AnalyticsEventInterface {
  category: string
  event: string
  source: string
  eventId: string
  sessionId: string

  company: {
    companyId: number
    country: string
  }
  subscription: {
    planId: string
    planName: string
    status: string
    invoiceQuotaLeft: number
  }
  user: {
    userId: number
    language: string
  }
  featureFlags?: ExperimentInterface[] | null
  payload: AnalyticsPayloadInterface
}

/**
 * Analytics data class, once this has been inited you can use it by calling Analytics.trackEvent
 */
export abstract class Analytics {
  private static getState: () => AnalyticsStateRequirementsInterface

  protected static generateGUID(): string {
    // https://stackoverflow.com/a/44078785/4265633
    // Unique to 100% if more than 1ms apart, if generated at same time risk of collision is 1 in a 10^15
    return Math.random().toString(36).substring(2) + new Date().getTime().toString(36)
  }

  public static initialize(containerId: string, getState: () => AnalyticsStateRequirementsInterface): void {
    if (containerId === '') {
      return
    }

    if (typeof window !== 'undefined' && typeof window.dataLayer === 'undefined') {
      window.dataLayer = []
    }

    this.getState = getState
  }

  /**
   * Sends an analytics event to Tag Manager, event always inlucdes a base payload that contains
   * {AnalyticsEventInterface} data for currently logged in user. Additional event data can be sent
   * by providing the payload parameter
   *
   * @param {string} name Name of the event that will be sent to analytics
   * @param {AnalyticsPayloadInterface} payload Additional event payload to be sent with this event
   */
  public static trackEvent(name: string, payload?: AnalyticsPayloadInterface): void {
    if (typeof window !== 'undefined' && typeof window.dataLayer !== 'undefined') {
      try {
        window.dataLayer?.push(Analytics.buildEvent(name, payload))
      } catch (e) {
        console.error(`Could not create analytics event for ui:${name}`, e)
      }
    } else {
      console.error('Analytics not initialized, event not sent')
    }
  }

  protected static buildEvent(name: string, payload: AnalyticsPayloadInterface = {}): AnalyticsEventInterface {
    const state = Analytics.getState()
    const { subscriptionManagement, token, experiments } = state
    const { companySubscription } = subscriptionManagement || ({} as SubscriptionManagementStateInterface)

    const subscription = companySubscription ? companySubscription.subscription : null
    const premiumQuota = subscription?.quotas.find((quota) => quota.type === QuotaTypes.INVOICE)
    const quotaLeft = premiumQuota ? premiumQuota.total - premiumQuota.used : null

    return {
      payload,
      company: {
        country: token.country,
        companyId: token.prn.companyId,
      },
      subscription: {
        status: subscription?.payment ? subscription.payment.status : null,
        planId: subscription?.plan ? subscription.plan.id : null,
        planName: subscription?.plan ? subscription.plan.name : null,
        invoiceQuotaLeft: quotaLeft,
      },
      user: {
        userId: token.prn.userId,
        language: token.locale.split('_')[0],
      },
      eventId: Analytics.generateGUID(),
      sessionId: token.jti,
      category: `ui:${name}`,
      event: `ui:${name}`,
      source: 'UI',
      featureFlags: experiments?.experiments,
    }
  }
}

export const checkPlacement = (pathname: string) => {
  const pathPart = pathname.split('/')[1]
  return pathPart === '' ? 'dashboard' : PlacementsFromPath[pathPart] || 'unknown'
}
